import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  DocumentBuyingSignalsQuery,
  DocumentsSearchRequest,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

// Keyed off the request object (React Query serializes it structurally) so each distinct filter set
// has its own cache entry — the backend computes the matching operationId from (teamId, filters).
export const documentBuyingSignalsQueryKey = (request: DocumentsSearchRequest): QueryKey => [
  "documentBuyingSignals",
  request,
];

export const documentBuyingSignalsQuery = graphql(`
  query documentBuyingSignals($request: DocumentsSearchRequest!) {
    documentBuyingSignals(DocumentsSearchRequest: $request) {
      operationId
      status
      result {
        documentCount
        leads {
          documentId
          documentName
          buyerName
          buyerId
          leadTitle
          leadMotivation
          matchingText
          leadType
          pageNumbers
          chunkNumber
          locations {
            pageNumber
            bbox {
              x
              y
              width
              height
            }
          }
        }
      }
    }
  }
`);

export type DocumentBuyingSignalsResponse = DocumentBuyingSignalsQuery["documentBuyingSignals"];
export type DocumentBuyingSignalsResult = NonNullable<DocumentBuyingSignalsResponse["result"]>;
export type DocumentBuyingSignalLead = DocumentBuyingSignalsResult["leads"][number];

export function useDocumentBuyingSignals(
  request: DocumentsSearchRequest,
  options?: UseQueryOptions<
    DocumentBuyingSignalsQuery,
    unknown,
    DocumentBuyingSignalsQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    documentBuyingSignalsQueryKey(request),
    documentBuyingSignalsQuery,
    [{ request }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return { data: data?.documentBuyingSignals, ...rest };
}
